import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { downloadBlob } from 'utils/exports';

import { CaseOrganizationExecutionMode } from './caseOrganizationPlan';
import {
  buildManifestWorkbookReferences,
  createManifestWorkbook,
  projectManifestWorkbookPlan,
} from './manifestWorkbook';
import { encodeManifestWorkbookContext } from './manifestWorkbookContext';
import { buildManifestWorkbookInstructions } from './manifestWorkbookInstructions';

interface UseManifestWorkbookExportOptions {
  loading: boolean;
  authorized: boolean;
  open: boolean;
  canSwitchLabs: boolean;
  currentPlanSignature: MutableRefObject<string>;
  executionMode: CaseOrganizationExecutionMode;
  existingProcedures: Procedure[];
  exportedPlanSignature: MutableRefObject<string | null>;
  labId: string;
  procedureFieldContext: ProceduresFieldsContext;
  projection: ReturnType<typeof projectManifestWorkbookPlan> | null;
  studyId: string;
}

export const useManifestWorkbookExport = ({
  loading,
  authorized,
  open,
  canSwitchLabs,
  currentPlanSignature,
  executionMode,
  existingProcedures,
  exportedPlanSignature,
  labId,
  procedureFieldContext,
  projection,
  studyId,
}: UseManifestWorkbookExportOptions) => {
  const auth = useAppSelector((state) => state.auth);
  const scope = JSON.stringify([
    open,
    labId,
    studyId,
    auth.profile?.userId,
    auth.generationId,
    authorized,
    canSwitchLabs,
  ]);
  const scopeState = useRef({ scope, generation: 0 });
  if (scopeState.current.scope !== scope) {
    scopeState.current = { scope, generation: scopeState.current.generation + 1 };
  }
  useEffect(
    () => () => {
      scopeState.current.generation += 1;
    },
    []
  );
  const [exportingGeneration, setExportingGeneration] = useState<number | null>(null);
  const [exportError, setExportError] = useState<{ generation: number; message: string } | null>(null);
  const [queuedGeneration, setQueuedGeneration] = useState<number | null>(null);
  const isDownloadQueued = queuedGeneration === scopeState.current.generation;
  const isExportingWorkbook = exportingGeneration === scopeState.current.generation || isDownloadQueued;
  const workbookExportError = exportError?.generation === scopeState.current.generation ? exportError.message : null;

  const exportCurrentPlan = async () => {
    if (!authorized || !open || loading || !projection?.rows) return;
    const generation = scopeState.current.generation;
    const signature = currentPlanSignature.current;
    setExportingGeneration(generation);
    setExportError(null);
    try {
      const blob = await createManifestWorkbook({
        rows: projection.rows,
        recovery: projection.recovery,
        references: buildManifestWorkbookReferences(procedureFieldContext),
        instructions: [
          ...buildManifestWorkbookInstructions(existingProcedures),
          ...(projection.recovery.length
            ? [
                'Plan recovery lists values to check after uploading. Restore the intended values in the case assignment before applying changes. Changes made in Plan recovery are not applied to the case assignment.',
              ]
            : []),
        ],
        context: encodeManifestWorkbookContext({
          studyId,
          labId,
          studyName: null,
          labName: null,
          handling: executionMode,
        }),
      });
      if (generation !== scopeState.current.generation) return;
      downloadBlob(blob, 'case-plan.xlsx');
      exportedPlanSignature.current = signature;
    } catch {
      if (generation === scopeState.current.generation)
        setExportError({ generation, message: 'Could not download the case assignment. Try again.' });
    } finally {
      if (generation === scopeState.current.generation) setExportingGeneration(null);
    }
  };

  // Clicking while data is still loading queues the download instead of blocking the click.
  const downloadCurrentPlan = async () => {
    if (!authorized || !open || isExportingWorkbook) return;
    if (loading) {
      setQueuedGeneration(scopeState.current.generation);
      return;
    }
    await exportCurrentPlan();
  };

  useEffect(() => {
    if (!isDownloadQueued || loading) return;
    setQueuedGeneration(null);
    void exportCurrentPlan();
  }, [isDownloadQueued, loading]);

  const dismissWorkbookExportError = () => {
    setExportError((currentError) => (currentError === exportError ? null : currentError));
  };

  return { downloadCurrentPlan, dismissWorkbookExportError, isExportingWorkbook, workbookExportError };
};
